import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  ReferralProductState,
  ReferralProduct,
  ProductDetailResponse,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_REFERALSERVICE_PERFIX || "/api", 
]
  .filter(Boolean)
  .join("");

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Async Thunks
export const fetchReferralProducts = createAsyncThunk(
  "refProduct/fetchReferralProducts",
  async (
    params: { tag?: string; region?: string } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get("/public/referral-products", {
        params,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch referral products",
      );
    }
  },
);

export const fetchReferralProductDetail = createAsyncThunk(
  "refProduct/fetchReferralProductDetail",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/public/referral-products/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch product detail",
      );
    }
  },
);

const initialState: ReferralProductState = {
  products: [],
  currentProduct: null,
  loading: false,
  error: null,
};

const referralProductSlice = createSlice({
  name: "refProduct",
  initialState,
  reducers: {
    clearRefProductError: (state) => {
      state.error = null;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchReferralProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReferralProducts.fulfilled,
        (state, action: PayloadAction<ReferralProduct[]>) => {
          state.loading = false;
          state.products = action.payload;
        },
      )
      .addCase(fetchReferralProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Product Detail
      .addCase(fetchReferralProductDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReferralProductDetail.fulfilled,
        (state, action: PayloadAction<ProductDetailResponse>) => {
          state.loading = false;
          state.currentProduct = action.payload;
        },
      )
      .addCase(fetchReferralProductDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearRefProductError, clearCurrentProduct } =
  referralProductSlice.actions;
export default referralProductSlice.reducer;
